import type { QueuedChatMessage } from './chatInstancesStore';
import type {
  ChatInstancesSyncSlice,
  ChatTabsSyncSlice,
  LayoutSyncSlice,
  ProjectSyncSlice,
  QueuedMessageSyncEntry,
} from './crossWindowSync.types';
import {
  CHAT_TABS_SYNC_KEYS,
  LAYOUT_SYNC_KEYS,
  PROJECT_SYNC_KEYS,
} from './crossWindowSync.types';
import { ChatInstancesSyncPatchSchema } from '../electron/detached-window-schemas';

export function pickSyncSlice<
  TSource extends object,
  const TKeys extends readonly (keyof TSource & string)[],
>({
  source,
  keys,
}: {
  source: TSource;
  keys: TKeys;
}): Pick<TSource, TKeys[number]> {
  const result = {} as Pick<TSource, TKeys[number]>;

  for (const key of keys) {
    if (Object.hasOwn(source, key)) {
      result[key] = source[key];
    }
  }

  return result;
}

export function pickLayoutSyncSlice(source: LayoutSyncSlice): LayoutSyncSlice {
  return pickSyncSlice({ source, keys: LAYOUT_SYNC_KEYS });
}

export function pickChatTabsSyncSlice(
  source: ChatTabsSyncSlice,
): ChatTabsSyncSlice {
  return pickSyncSlice({ source, keys: CHAT_TABS_SYNC_KEYS });
}

export function pickProjectSyncSlice(
  source: ProjectSyncSlice,
): ProjectSyncSlice {
  return pickSyncSlice({ source, keys: PROJECT_SYNC_KEYS });
}

export function diffSyncSlice<TSlice extends Record<string, unknown>>({
  current,
  previous,
}: {
  current: TSlice;
  previous: TSlice;
}): Partial<TSlice> | null {
  const patch: Partial<TSlice> = {};
  let hasChanges = false;

  for (const key of Object.keys(current) as Array<keyof TSlice & string>) {
    const currentValue = current[key];
    const previousValue = previous[key];

    if (JSON.stringify(currentValue) !== JSON.stringify(previousValue)) {
      patch[key] = currentValue;
      hasChanges = true;
    }
  }

  return hasChanges ? patch : null;
}

export function serializeQueuedMessagesForSync(
  queuedMessages: Map<string, QueuedChatMessage[]>,
): ChatInstancesSyncSlice {
  return {
    queuedMessages: Array.from(queuedMessages.entries()).map(
      ([chatId, messages]) => [chatId, messages.map(toQueuedMessageSyncEntry)],
    ),
  };
}

function toQueuedMessageSyncEntry(
  message: QueuedChatMessage,
): QueuedMessageSyncEntry {
  return {
    id: message.id,
    message: message.message,
    createdAt: message.createdAt,
    state: message.state,
  };
}

export function parseChatInstancesSyncPatch(
  patch: Record<string, unknown>,
): ChatInstancesSyncSlice | null {
  const parsed = ChatInstancesSyncPatchSchema.safeParse(patch);

  if (!parsed.success) {
    return null;
  }

  if (parsed.data.queuedMessages === undefined) {
    return null;
  }

  return { queuedMessages: parsed.data.queuedMessages };
}

export function restoreQueuedMessagesFromSync(
  entries: ChatInstancesSyncSlice['queuedMessages'],
): Map<string, QueuedChatMessage[]> {
  return new Map(
    entries.map(([chatId, messages]) => [
      chatId,
      messages.map(toQueuedChatMessage),
    ]),
  );
}

function toQueuedChatMessage(entry: QueuedMessageSyncEntry): QueuedChatMessage {
  return {
    id: entry.id,
    message: entry.message,
    createdAt: entry.createdAt,
    state: entry.state,
  };
}

function sanitizeSyncPatch<const TKeys extends readonly string[]>({
  patch,
  keys,
}: {
  patch: Record<string, unknown>;
  keys: TKeys;
}): Partial<Record<TKeys[number], unknown>> | null {
  const entries = keys
    .filter((key) => Object.hasOwn(patch, key))
    .map((key) => [key, patch[key]] as const);

  if (entries.length === 0) {
    return null;
  }

  return Object.fromEntries(entries) as Partial<Record<TKeys[number], unknown>>;
}

export function sanitizeLayoutSyncPatch(
  patch: Record<string, unknown>,
): Partial<LayoutSyncSlice> | null {
  return sanitizeSyncPatch({
    patch,
    keys: LAYOUT_SYNC_KEYS,
  }) as Partial<LayoutSyncSlice> | null;
}

export function sanitizeChatTabsSyncPatch(
  patch: Record<string, unknown>,
): Partial<ChatTabsSyncSlice> | null {
  return sanitizeSyncPatch({
    patch,
    keys: CHAT_TABS_SYNC_KEYS,
  }) as Partial<ChatTabsSyncSlice> | null;
}

export function sanitizeProjectSyncPatch(
  patch: Record<string, unknown>,
): Partial<ProjectSyncSlice> | null {
  return sanitizeSyncPatch({
    patch,
    keys: PROJECT_SYNC_KEYS,
  }) as Partial<ProjectSyncSlice> | null;
}
